import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import API from "../../api/api";
import { IObjectIdUser } from "./user";
import { IObjectVerwaltung } from "./objectVerwaltung";
import { setAdminLoader } from "./admin";
import { setErrorMessage, setIsLoading, setIsToasterError } from "./app";

export interface IEwgLoginResponse {
  error: boolean;
  text: string;
  user: IObjectIdUser;
  token: string;
  defaultAdress: IObjectVerwaltung;
}

export interface IEinwohnergleichwertabfrageItems {
  facility: string;
  quantity: string;
  typeSpace: string;
  companyName: string;
  quantityOfHalfDayEymployees: string;
  quantityOfPartTimeEmployees: string;
}

export interface IEinwohnergleichwertabfrageItemsResponse extends IEinwohnergleichwertabfrageItems {
  isQuantityPlausible: boolean;
  quantityExceeded: "above" | "belove" | "";
}

export interface IEwgCreateBody {
  einwohnergleichwertabfrageItems: IEinwohnergleichwertabfrageItems[];
  email: string;
  idNumber: string;
  phoneNumber: string;
  additionalComment: string;
  aktenzeichen: string;
}

export interface IEwg extends IEwgCreateBody {
  status?:
    | "check"
    | "accepted"
    | "declined"
    | "inWork"
    | "error"
    | "plausibilityCheck";
  createdAt: string;
  homeNumber: string;
  street: string;
  place: string;
  postCode: number;
  einwohnergleichwertabfrageItems: IEinwohnergleichwertabfrageItemsResponse[];
}

interface IState {
  client: {
    isLoading: boolean;
    loginAnswer: {
      error: boolean;
      text?: string;
    };
    user: null | IObjectIdUser;
    token: string;
    defaultAdress: null | IObjectVerwaltung;
  };
  admin: {
    ewgData: IEwg[];
    ewgDataTotalCount: number;
    ewgItemInfo: IEwg | null;
    isEwgDataLoading: boolean;
  };
}

export const ewgLogin = createAsyncThunk(
  "ewg/ewgLogin",
  async ({
    aktenzeichen,
    kassenzeichen,
  }: {
    kassenzeichen: string | number;
    aktenzeichen: string | number;
    navigate: () => void;
  }) => {
    const { data } = await API.ewg.post.ewgLogin({
      aktenzeichen,
      kassenzeichen,
    });
    return data;
  },
);

export const createEwg = createAsyncThunk(
  "ewg/createEwg",
  async (data: IEwgCreateBody & { success: () => void }, { dispatch }) => {
    let err;
    const { success, ...rest } = data;
    try {
      const res = await API.ewg.post.createEwg(rest);
      dispatch(setIsLoading(true));
      return res;
    } catch (error) {
      err = error;
      dispatch(setIsToasterError(true));
    } finally {
      dispatch(setIsLoading(false));
      if (!err) {
        success();
      }
    }
  },
);

export const getAdminEwgData = createAsyncThunk(
  "sperrmuell/admin/getAdminEwgData",
  async (
    {
      page,
      direction,
      searchValue,
      sort,
      status,
      signal,
      pageSize,
      endDate,
      startDate,
    }: {
      sort?: string;
      direction?: string;
      searchValue?: string | undefined;
      page: number;
      pageSize?: number;
      status?: string[];
      signal: AbortSignal;
      startDate?: Date | null | string;
      endDate?: Date | null | string;
    },
    thunkApi,
  ) => {
    try {
      thunkApi.dispatch(setAdminLoader(true));
      const sperrmuelData = await API.ewg.get.getEwgData({
        direction: direction || undefined,
        pageSize,
        page,
        searchValue: searchValue || undefined,
        sort: sort || "",
        status,
        signal,
        startDate,
        endDate,
      });
      thunkApi.dispatch(setAdminLoader(false));
      return sperrmuelData;
    } catch (error: any) {
      thunkApi.dispatch(setErrorMessage(error?.response?.data?.message));
      thunkApi.dispatch(setAdminLoader(false));
      thunkApi.dispatch(setIsToasterError(true));
    }
  },
);

export const getAdminEwgDataById = createAsyncThunk(
  "sperrmuell/admin/getEwgDataById",
  async (id: string, thunkApi) => {
    try {
      thunkApi.dispatch(setAdminLoader(true));

      const data = await API.ewg.get.getEwgById(id);

      thunkApi.dispatch(setAdminLoader(false));

      return data;
    } catch (error) {
      thunkApi.dispatch(setAdminLoader(false));
      thunkApi.dispatch(setIsToasterError(true));
    }
  },
);

export const updateAdminEwgDataById = createAsyncThunk(
  "sperrmuell/admin/updateAdminEwgDataById",
  async ({ id, status }: { id: string; status: string }, thunkApi) => {
    try {
      thunkApi.dispatch(setAdminLoader(true));

      const data = await API.ewg.put.updateAdminEwgDataById({ id, status });

      thunkApi.dispatch(setAdminLoader(false));

      return data;
    } catch (error) {
      thunkApi.dispatch(setAdminLoader(false));
      thunkApi.dispatch(setIsToasterError(true));
    }
  },
);

const initialState: IState = {
  client: {
    isLoading: false,
    loginAnswer: {
      error: false,
      text: "",
    },
    defaultAdress: null,
    token: "",
    user: null,
  },
  admin: {
    ewgData: [],
    ewgDataTotalCount: 0,
    ewgItemInfo: null,
    isEwgDataLoading: false,
  },
};

const ewgSlice = createSlice({
  name: "ewg",
  initialState,
  reducers: {
    clearState: (state) => {
      state = initialState;
    },
    clearAdminItemById: (state) => {
      state.admin.ewgItemInfo = null;
    },
    clearAdminEwgData: (state) => {
      state.admin.ewgData = [];
      state.admin.ewgDataTotalCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(ewgLogin.pending, (state) => {
        state.client.isLoading = true;
      })
      .addCase(ewgLogin.fulfilled, (state, action) => {
        action.meta.arg.navigate();
        const { defaultAdress, error, text, token, user } = action.payload;
        state.client.isLoading = false;
        if (action.payload) {
          if (!error) {
            state.client.token = token;
            state.client.defaultAdress = defaultAdress;
            state.client.user = user;
            action.meta.arg.navigate();
          }
        } else {
          state.client.loginAnswer.error = true;
          state.client.loginAnswer.text = text;
        }
      })
      .addCase(ewgLogin.rejected, (state, act) => {
        state.client.isLoading = false;
        state.client.loginAnswer.error = true;
        state.client.loginAnswer.text =
          "Die Kombination aus Aktenzeichen und Passwort passt zu keinem Konto";
      });
    builder
      .addCase(createEwg.pending, (state, action) => {
        state.client.isLoading = true;
      })
      .addCase(createEwg.fulfilled, (state, action) => {
        state.client.isLoading = false;
      })
      .addCase(createEwg.rejected, (state, action) => {
        state.client.isLoading = false;
      });
    builder
      .addCase(getAdminEwgData.pending, (state) => {
        state.admin.isEwgDataLoading = true;
      })
      .addCase(getAdminEwgData.fulfilled, (state, action) => {
        state.admin.isEwgDataLoading = false;
        if (action.payload) {
          const { data, totalCount } = action.payload;
          state.admin.ewgDataTotalCount = totalCount;
          state.admin.ewgData = data;
        }
      })
      .addCase(getAdminEwgData.rejected, (state) => {
        state.admin.isEwgDataLoading = false;
      });
    builder
      .addCase(getAdminEwgDataById.pending, (state) => {
        state.admin.isEwgDataLoading = true;
      })
      .addCase(getAdminEwgDataById.fulfilled, (state, action) => {
        state.admin.isEwgDataLoading = false;
        if (action.payload) {
          state.admin.ewgItemInfo = action.payload;
        }
      })
      .addCase(getAdminEwgDataById.rejected, (state) => {
        state.admin.isEwgDataLoading = false;
      });
    builder
      .addCase(updateAdminEwgDataById.pending, (state) => {
        state.admin.isEwgDataLoading = true;
      })
      .addCase(updateAdminEwgDataById.fulfilled, (state, action) => {
        state.admin.isEwgDataLoading = false;
        if (action.payload) {
          state.admin.ewgItemInfo = action.payload;
        }
      })
      .addCase(updateAdminEwgDataById.rejected, (state) => {
        state.admin.isEwgDataLoading = false;
      });
  },
});

export const { clearAdminEwgData, clearAdminItemById, clearState } =
  ewgSlice.actions;

export const ewgReducer = ewgSlice.reducer;
